
import { useState, useRef, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Phone, ArrowLeft, RotateCcw, Check } from 'lucide-react'
import { Button, H2, Body, ButtonVariant } from '@meiuca/ui-components'

interface PhoneVerificationFormProps {
  phone: string
  onVerificationComplete: (code: string) => void
  onResendCode: () => void
  onBack: () => void
  isLoading?: boolean
  isResending?: boolean
  error?: string | null
}

export function PhoneVerificationForm({
  phone,
  onVerificationComplete,
  onResendCode,
  onBack,
  isLoading = false,
  isResending = false,
  error
}: PhoneVerificationFormProps) {
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [timeLeft, setTimeLeft] = useState(120) // 2 minutes
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  // Countdown timer
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [timeLeft])

  // Format phone for display
  const formatPhoneDisplay = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '')
    if (cleaned.length === 11) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`
    }
    return phone
  }

  const handleInputChange = (index: number, value: string) => {
    // Only allow numbers
    if (!/^\d*$/.test(value)) return

    const newCode = [...code]
    newCode[index] = value.slice(-1) // Only take the last character
    setCode(newCode)

    // Move to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }

    // Auto-submit when all fields are filled
    if (newCode.every(digit => digit !== '') && newCode.join('').length === 6) {
      onVerificationComplete(newCode.join(''))
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedText = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    const newCode = [...code]
    
    for (let i = 0; i < pastedText.length; i++) {
      if (i < 6) {
        newCode[i] = pastedText[i]
      }
    }
    
    setCode(newCode)
    
    // Focus on the next empty input or the last input
    const nextEmptyIndex = newCode.findIndex(digit => digit === '')
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex
    inputRefs.current[focusIndex]?.focus()

    // Auto-submit if complete
    if (newCode.every(digit => digit !== '') && newCode.join('').length === 6) {
      onVerificationComplete(newCode.join(''))
    }
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const handleResendCode = () => {
    setTimeLeft(120)
    setCode(['', '', '', '', '', ''])
    onResendCode()
    inputRefs.current[0]?.focus()
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full max-w-md mx-auto"
    >
      <div className="text-center mb-8">
        <motion.div
          className="w-16 h-16 bg-gradient-to-br from-brand-secondary to-brand-primary rounded-2xl mx-auto mb-6 flex items-center justify-center"
          whileHover={{ scale: 1.05 }}
          transition={{ type: 'spring', stiffness: 300 }}
        >
          <Phone className="w-8 h-8 text-white" />
        </motion.div>
        
        <H2 className="mb-2">Verificar telefone</H2>
        <Body className="text-text-secondary mb-2">
          Enviamos um código de 6 dígitos para
        </Body>
        <Body className="font-semibold text-text-primary">
          {formatPhoneDisplay(phone)}
        </Body>
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="mb-6 p-4 bg-background-negative border border-red-200 rounded-lg text-center"
        >
          <Body className="text-negative text-sm">{error}</Body>
        </motion.div>
      )}

      <div className="space-y-6">
        {/* Code Input */}
        <div className="space-y-4">
          <Body className="text-center text-text-secondary text-sm">
            Digite o código de verificação
          </Body>
          
          <div className="flex justify-center gap-3">
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => { inputRefs.current[index] = el }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                className="w-12 h-12 text-center text-xl font-semibold border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all"
                disabled={isLoading}
              />
            ))}
          </div>
        </div>

        {/* Timer and Resend */}
        <div className="text-center space-y-3">
          {timeLeft > 0 ? (
            <Body className="text-text-secondary text-sm">
              Reenviar código em {formatTime(timeLeft)}
            </Body>
          ) : (
            <Button
              variant={ButtonVariant.PRIMARY}
              buttonStyle="clear"
              onClick={handleResendCode}
              loading={isResending}
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              {isResending ? 'Reenviando...' : 'Reenviar código'}
            </Button>
          )}
        </div>

        {/* Manual Submit Button (if needed) */}
        {code.every(digit => digit !== '') && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Button
              variant={ButtonVariant.PRIMARY}
              onClick={() => onVerificationComplete(code.join(''))}
              loading={isLoading}
              className="w-full"
            >
              {isLoading ? (
                'Verificando...'
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Verificar código
                </>
              )}
            </Button>
          </motion.div>
        )}

        {/* Back Button */}
        <div className="text-center">
          <Button
            variant={ButtonVariant.PRIMARY}
            buttonStyle="clear"
            onClick={onBack}
            disabled={isLoading}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar ao cadastro
          </Button>
        </div>
      </div>
    </motion.div>
  )
}